import addQueryObject from "../helpers/addQueryObject";
import deepClone from "../helpers/deepClone";
import ensureErrorType from "../helpers/ensureErrorType";
import flattenObject from "../helpers/flattenObject";
import isNullOrEmpty from "../helpers/isNullOrEmpty";
import decodeJson from "../utils/Json";
import mergeObjects from "../utils/Merge";
import {HttpClientError} from "./HttpClientError.js";
import {HttpRequest} from "./HttpRequest.js";
import {DefaultHttpRequestOptions, HttpRequestOptions} from "./HttpRequestOptions.js";
import {HttpRequestResult} from "./HttpRequestResult.js";
import {ProblemDetails} from "./ProblemDetails.js";
import {ProxyResponse} from "./ProxyResponse";

const _acceptHeader: string = "accept";
const _contentTypeHeader: string = "content-type";
const _jsonHeader: string = "application/json";
const _jsonPatchHeader: string = "application/json-patch+json";
const _retryStatusCodes = new Set([
	408, // Request Timeout
	// 409, // Conflict
	425, // Too Early (Experimental)
	429, // Too Many Requests
	500, // Internal Server Error
	502, // Bad Gateway
	503, // Service Unavailable
	504 // Gateway Timeout
]);

interface CacheEntry {
	result: HttpRequestResult;
	timestamp: number;
	expiresAt: number;
}

function isInternalBodyType(body: any): boolean {
	if (body == null) {
		return false;
	}
	return typeof body === "string" || body instanceof FormData || body instanceof ArrayBuffer || body instanceof URLSearchParams || body instanceof ReadableStream || body instanceof Blob;
}

function isProblemDetails(response: Response): boolean {
	let contentType = response.headers.get(_contentTypeHeader);
	return contentType?.includes("problem+json") === true || contentType?.includes("application/json") === true;
}

function methodHasBody(method: string): boolean {
	return method === "POST" || method === "PUT" || method === "PATCH" || method === "DELETE";
}

function resultFromError(error: Error, request: RequestInit, url?: string): HttpRequestResult {
	return {
		request,
		response: null!,
		error: HttpClient.errorFromError(error, null, url)
	};
}

async function resultToRequestResult(request: RequestInit, response: Response): Promise<HttpRequestResult> {
	if (response.ok) {
		return {
			request,
			response
		};
	}

	let text = await response.text();
	let problemDetails: ProblemDetails = {
		detail: text,
		status: response.status,
		title: response.statusText
	};

	if (isProblemDetails(response)) {
		problemDetails = decodeJson(text) || problemDetails;
	}

	return {
		request,
		response,
		error: new HttpClientError(problemDetails, request, response)
	};
}

function getRequestKey(url: string, method: string, body: any, options: HttpRequestOptions | null): string {
	return `${method}:${url}:${JSON.stringify(body)}:${JSON.stringify(options)}`;
}

async function executeRequest(url: string, method: string, request: RequestInit, requestData: HttpRequest, options: HttpRequestOptions): Promise<HttpRequestResult> {
	let retries: number;
	if (options.retry === false) {
		retries = 0;
	} else if (typeof options.retry === "number") {
		retries = options.retry;
	} else {
		retries = methodHasBody(method) ? 0 : 1;
	}

	let error: HttpRequestResult;
	do {
		try {
			return await resultToRequestResult(request, new ProxyResponse(await fetch(url, request)));

		} catch (__e) {
			error = resultFromError(ensureErrorType(__e), request, url);
			const responseCode = error.response?.status || 500;
			const shouldRetry = Array.isArray(options.retryStatusCodes) ? options.retryStatusCodes.includes(responseCode) : _retryStatusCodes.has(responseCode);

			if (error.error?.name === "AbortError" || !shouldRetry || retries <= 0) {
				return error;
			}

			const retryDelay = typeof options.retryDelay === "function" ? options.retryDelay(requestData) : options.retryDelay || 0;
			if (retryDelay > 0) {
				await new Promise(resolve => setTimeout(resolve, retryDelay));
			}

			--retries;
		}
	} while (retries >= 0);

	return error;
}

export type HttpClientRequestMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "HEAD" | "OPTIONS" | "TRACE";


class HttpClient {
	private readonly _baseUrl: string;
	private readonly _cache: Map<string, CacheEntry>;
	private readonly _defaultOptions: DefaultHttpRequestOptions;
	private readonly _pendingRequests: Map<string, Promise<HttpRequestResult>>;
	private readonly _pipelines: ((request: HttpRequest, next: () => Promise<HttpRequestResult>) => Promise<HttpRequestResult>)[];

	protected constructor(baseUrl: string, defaults?: DefaultHttpRequestOptions) {
		this._baseUrl = baseUrl;
		this._pipelines = [];
		this._defaultOptions = defaults || {ttl: 0};
		this._cache = new Map();
		this._pendingRequests = new Map();
	}

	fetch(url: string, method: HttpClientRequestMethod, body: any | null, requestOptions: HttpRequestOptions | null): Promise<HttpRequestResult> {
		// Never mutate the caller's options: a reused options object would accumulate the defaults
		// and the injected accept/content-type headers across requests. Top level + headers copied;
		// signal survives by reference.
		const options = mergeObjects(this._defaultOptions, {...requestOptions, headers: {...requestOptions?.headers}});
		const dedupable = !isInternalBodyType(body);
		const requestKey = dedupable ? getRequestKey(url, method, body, options) : null;
		const shareKey = requestKey != null && requestOptions?.signal == null ? requestKey : null;
		const now = Date.now();
		const ttl = options.ttl ?? 0;

		if (requestKey != null && method === "GET" && ttl > 0) {
			const cachedEntry = this._cache.get(requestKey);
			if (cachedEntry && cachedEntry.expiresAt > now) {
				return Promise.resolve(cachedEntry.result);
			}
			if (cachedEntry) {
				this._cache.delete(requestKey);
			}
		}

		if (shareKey != null && this._pendingRequests.has(shareKey)) {
			return this._pendingRequests.get(shareKey)!;
		}

		url = this._getUrl(method, url, body, options);
		const requestHeaders = options.headers ??= {};
		const request: RequestInit = {
			cache: options.cache,
			credentials: options.credentials,
			headers: requestHeaders,
			method,
			mode: options.mode,
			signal: requestOptions?.signal
		};

		const useJson = !isInternalBodyType(body);
		if (useJson) {
			requestHeaders[_acceptHeader] ??= _jsonHeader;
		}

		if (methodHasBody(method)) {
			if (useJson) {
				requestHeaders[_contentTypeHeader] ??= method === "PATCH" ? _jsonPatchHeader : _jsonHeader;
			}
			body = useJson ? body == null ? null : JSON.stringify(body) : body;
			if (body != null) {
				request.body = body;
			}
		}

		const requestData: HttpRequest = {
			method,
			body,
			headers: requestHeaders
		};

		let result: () => Promise<HttpRequestResult> = () => executeRequest(url, method, request, requestData, options);

		const pipelines = this._pipelines;
		for (let i = pipelines.length - 1; i >= 0; --i) {
			const copy = result;
			const pipeline = pipelines[i];
			result = () => pipeline(requestData, copy);
		}

		const requestPromise = result().then(result => {
			if (requestKey != null && method === "GET" && ttl > 0 && !result.error) {
				//opportunistic sweep so expired keys never accumulate unboundedly
				for (const [key, entry] of this._cache) {
					if (entry.expiresAt <= now) this._cache.delete(key);
				}
				this._cache.set(requestKey, {
					result,
					timestamp: now,
					expiresAt: now + ttl
				});
			}

			return result;

		}).finally(() => {
			if (shareKey != null) this._pendingRequests.delete(shareKey);
		});

		if (shareKey != null) this._pendingRequests.set(shareKey, requestPromise);
		return requestPromise;
	}

	protected addPipeline(pipeline: (request: HttpRequest, next: () => Promise<HttpRequestResult>) => Promise<HttpRequestResult>): void {
		this._pipelines.push(pipeline);
	}

	protected isJson(response: Response): boolean {
		const contentType = response.headers.get(_contentTypeHeader);
		return contentType?.includes(_jsonHeader) === true;
	}

	private _getUrl(method: HttpClientRequestMethod, requestUrl: string, body: object | undefined, options: HttpRequestOptions): string {
		if (!isNullOrEmpty(this._baseUrl)) {
			if (requestUrl.charAt(0) !== "/") {
				requestUrl = this._baseUrl + requestUrl;
			}
		}

		let query = options.query;
		if (method === "GET" && body != null) {
			query = query == null ? {} : deepClone(query);
			flattenObject(body, query);
		}

		return addQueryObject(requestUrl, query);
	}

	static errorFromError(error: Error, response: HttpRequestResult | null, url?: string): HttpClientError {
		const result = new HttpClientError({
			status: 0,
			title: error.message,
			type: error.name,
			instance: url ?? response?.response?.url
		}, response?.request ?? null, response?.response ?? null);
		// Keep the original name (e.g. "AbortError") so callers can tell aborts
		// apart from real failures without digging into problemDetails.
		result.name = error.name;
		return result;
	}
}

interface DefaultHttpClientResponse {
	response?: Response;
}

class DefaultHttpClient extends HttpClient {
	constructor(baseUrl: string, defaults?: DefaultHttpRequestOptions) {
		super(baseUrl, defaults);
	}

	async jsonFetch<T>(url: string, requestMethod: HttpClientRequestMethod, body: any | null = null, requestOptions: HttpRequestOptions | null = null, responseRef: DefaultHttpClientResponse | null = null): Promise<T> {
		const result = await this.fetch(url, requestMethod, body, requestOptions);
		if (responseRef != null) {
			responseRef.response = result.response;
		}

		if (result.error != null) {
			throw result.error;
		}

		const response = result.response;
		let text: string | null = null;

		try {
			text = await response.text();

		} catch (__e) {
			throw HttpClient.errorFromError(ensureErrorType(__e), result);
		}

		if (this.isJson(response)) {
			return decodeJson<T>(text)!;
		}

		if (response.status === 204) {
			return null!;
		}

		throw new HttpClientError({
			detail: text,
			status: response.status,
			title: "Not a json response"
		}, result.request, response);
	}

	async nullFetch(url: string, requestMethod: HttpClientRequestMethod, body: any | null = null, requestOptions: HttpRequestOptions | null = null, responseRef: DefaultHttpClientResponse | null = null): Promise<void> {
		const result = await this.fetch(url, requestMethod, body, requestOptions);
		if (responseRef != null) {
			responseRef.response = result.response;
		}

		if (result.error == null) {
			return;
		}

		throw result.error;
	}

	async textFetch(url: string, requestMethod: HttpClientRequestMethod, body: any | null = null, requestOptions: HttpRequestOptions | null = null, responseRef: DefaultHttpClientResponse | null = null): Promise<string> {
		const result = await this.fetch(url, requestMethod, body, requestOptions);
		if (responseRef != null) {
			responseRef.response = result.response;
		}

		if (result.error != null) {
			throw result.error;
		}

		const response = result.response;

		try {
			return await response.text();

		} catch (__e) {
			throw HttpClient.errorFromError(ensureErrorType(__e), result);
		}
	}

	async blobFetch(url: string, requestMethod: HttpClientRequestMethod, body: any | null = null, requestOptions: HttpRequestOptions | null = null, responseRef: DefaultHttpClientResponse | null = null): Promise<Blob> {
		const result = await this.fetch(url, requestMethod, body, requestOptions);
		if (responseRef != null) {
			responseRef.response = result.response;
		}

		if (result.error != null) {
			throw result.error;
		}

		const response = result.response;

		try {
			return await response.blob();

		} catch (__e) {
			throw HttpClient.errorFromError(ensureErrorType(__e), result);
		}
	}
}

export {HttpClient, DefaultHttpClient, DefaultHttpClientResponse};
